/** Base URL for customer-facing API calls (must match NEXT_PUBLIC_API_URL in env). */
export const CUSTOMER_API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1/api';

const API_URL = CUSTOMER_API_BASE_URL;

/** Customer web sends this on public category APIs so `show_in_website=false` rows are hidden; mobile omits for legacy behavior. */
export const WEBSITE_ONLY_QUERY_PARAM = 'website_only';

export interface CategoryType {
  id: string;
  name_en: string;
  name_ar: string;
  icon_url: string;
  is_new: boolean;
  is_default_selected: boolean;
}

/** Category types shown on the customer website (venues are mobile-only). */
export const WEBSITE_CATEGORY_TYPES = ['catering', 'themes-and-gifts'] as const;

/** Comma-separated string used as a query param to restrict to website-supported types. */
export const WEBSITE_CATEGORY_TYPES_PARAM = WEBSITE_CATEGORY_TYPES.join(',');

/** Types excluded from /categories hub tabs (venues live under /venues). */
const HIDDEN_CATEGORY_TYPE_IDS_ON_HUB = new Set<string>(['venues']);

export function filterCategoryTypesForHub<T extends { id: string }>(types: T[]): T[] {
  return types.filter((t) => !HIDDEN_CATEGORY_TYPE_IDS_ON_HUB.has(t.id));
}

export interface Subcategory {
  id: string;
  name_en: string;
  name_ar: string;
  description_en?: string;
  description_ar?: string;
  icon?: string;
  image_url?: string;
  thumbnail_url?: string;
  cover_image?: string;
  category_id: string;
  is_active: boolean;
  meta_title_en?: string;
  meta_title_ar?: string;
  meta_description_en?: string;
  meta_description_ar?: string;
}

export interface Category {
  id: string;
  name_en: string;
  name_ar: string;
  description_en?: string;
  description_ar?: string;
  icon?: string;
  image_url?: string;
  thumbnail_url?: string;
  cover_image?: string;
  category_type?: string;
  sort_order?: number;
  is_active: boolean;
  subcategories?: Subcategory[];
  subcategories_count?: number;
  show_in_website?: boolean;
  meta_title_en?: string;
  meta_title_ar?: string;
  meta_description_en?: string;
  meta_description_ar?: string;
}

export interface VirtualCategoryItem {
  id: string;
  name_en: string;
  name_ar: string;
  description_en?: string;
  description_ar?: string;
  price: number;
  main_images?: string[];
  vendor_name_en?: string;
  vendor_name_ar?: string;
  vendor?: { icon?: string };
  is_favorite?: boolean;
  is_new?: boolean;
  is_on_sale?: boolean;
  is_trending?: boolean;
  item_rating?: number;
  discount?: {
    original_price: number;
    discounted_price: number;
  };
}

export interface VirtualCategory {
  id: string;
  name_en: string;
  name_ar: string;
  description_en?: string;
  description_ar?: string;
  image_url?: string;
  thumbnail_url?: string;
  cover_image?: string;
  sort_order?: number;
  is_active: boolean;
  show_in_website?: boolean;
  content_type?: 'categories' | 'items' | 'vendors';
  categories?: Category[];
  items?: Array<VirtualCategoryItem | string>;
}

export interface HomescreenCategory {
  id: string;
  name_en: string;
  name_ar: string;
  virtual_category_id?: string | null;
  category_id?: string | null;
  subcategory_id?: string | null;
  display_vendors?: boolean;
}

export async function getVirtualCategoriesForMenu(): Promise<VirtualCategory[]> {
  try {
    const params = new URLSearchParams({
      include_categories: 'true',
      preview_limit: '8',
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
    });

    const response = await fetch(`${API_URL}/virtual-categories?${params.toString()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (!data.success || !data.data) return [];

    const virtualCategories = (data.data as VirtualCategory[]).filter((virtualCategory) => {
      const contentType = virtualCategory.content_type || 'categories';
      return virtualCategory.is_active && virtualCategory.show_in_website !== false && (contentType === 'categories' || contentType === 'items');
    });

    return await Promise.all(
      virtualCategories.map(async (virtualCategory) => {
        const contentType = virtualCategory.content_type || 'categories';
        const hasOnlyItemIds =
          contentType === 'items' &&
          Array.isArray(virtualCategory.items) &&
          virtualCategory.items.some((item) => typeof item === 'string');

        if (!hasOnlyItemIds) return virtualCategory;

        const detailResponse = await fetch(
          `${API_URL}/virtual-categories/${virtualCategory.id}?include_categories=true&${WEBSITE_ONLY_QUERY_PARAM}=true`,
          { cache: 'no-store' }
        );

        if (!detailResponse.ok) return virtualCategory;

        const detailData = await detailResponse.json();
        return detailData.success && detailData.data ? detailData.data : virtualCategory;
      })
    );
  } catch (error) {
    console.error('Failed to fetch virtual categories for menu:', error);
    return [];
  }
}

export async function getWebsiteVirtualCategoriesForHub(): Promise<VirtualCategory[]> {
  return getVirtualCategoriesForMenu();
}

/**
 * Server-side fetch for a single homescreen category. Used to resolve filter params.
 */
export async function getHomescreenCategoryById(id: string): Promise<HomescreenCategory | null> {
  try {
    const response = await fetch(`${API_URL}/homescreen-cats/${id}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Failed to fetch homescreen category:', error);
    return null;
  }
}

/**
 * Server-side fetch for home/catering categories. Used for SSR.
 */
export async function getCateringOptions(): Promise<unknown[]> {
  try {
    const params = new URLSearchParams({
      include_categories: 'true',
      include_subcategories: 'true',
      category_type: 'catering',
      platform: 'web'
    });
    const response = await fetch(`${API_URL}/homescreen-cats?${params.toString()}`, {
      cache: 'no-store'
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (!data.success || !data.data) return [];

    return data.data || [];
  } catch (error) {
    console.error('Failed to fetch catering options:', error);
    return [];
  }
}

/**
 * Server-side fetch for category types. Used for SSR category page tabs.
 */
export async function getCategoryTypes(): Promise<CategoryType[]> {
  try {
    const response = await fetch(`${API_URL}/categories/types`, {
      cache: 'no-store',
    });

    if (!response.ok) return [];

    const data = await response.json();
    return filterCategoryTypesForHub(data.data || []);
  } catch (error) {
    console.error('Failed to fetch category types:', error);
    return [];
  }
}

/**
 * Server-side fetch for categories (catalog) with subcategories by category_type.
 * Uses GET /categories — not homescreen-cats.
 */
export async function getCategories(type?: string): Promise<Category[]> {
  try {
    const params = new URLSearchParams({
      include_subcategories: 'true',
      exclude_virtual_linked: 'true',
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
    });

    if (type && type !== 'all') {
      params.set('category_type', type);
    } else {
      // Default to all website-supported types (venues are mobile-only)
      params.set('category_type', WEBSITE_CATEGORY_TYPES_PARAM);
    }

    const response = await fetch(`${API_URL}/categories?${params.toString()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (!data.success || !data.data) return [];

    return data.data as Category[];
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }
}

/**
 * Server-side fetch for a single category with subcategories. Used for SSR/ISR.
 */
export async function getCategoryById(categoryId: string): Promise<any | null> {
  try {
    const qs = new URLSearchParams({
      include_subcategories: 'true',
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
    }).toString();
    const response = await fetch(`${API_URL}/categories/${categoryId}?${qs}`, {
      next: { revalidate: 300 },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Failed to fetch category:', error);
    return null;
  }
}

/**
 * Lightweight fetch for category SEO metadata only (no subcategories).
 * Matches ISR timing used by the category detail page.
 */
export async function fetchCategoryForMetadata(categoryId: string): Promise<Category | null> {
  try {
    const qs = new URLSearchParams({
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
    }).toString();
    const response = await fetch(`${API_URL}/categories/${categoryId}?${qs}`, {
      next: { revalidate: 300 },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Failed to fetch category for metadata:', error);
    return null;
  }
}

/**
 * Server-side fetch for a single subcategory. Used for SSR.
 */
export async function getSubcategoryById(subcategoryId: string): Promise<any | null> {
  try {
    const response = await fetch(
      `${API_URL}/subcategories/${subcategoryId}`,
      { cache: 'no-store' }
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Failed to fetch subcategory:', error);
    return null;
  }
}

/**
 * Fetch subcategory for SEO metadata (ISR). Same payload as detail fetch but cached for layout generateMetadata.
 */
export async function fetchSubcategoryForMetadata(subcategoryId: string): Promise<Subcategory | null> {
  try {
    const response = await fetch(`${API_URL}/subcategories/${subcategoryId}`, {
      next: { revalidate: 300 },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Failed to fetch subcategory for metadata:', error);
    return null;
  }
}

/**
 * Server-side fetch for items by subcategory. Used for SSR.
 */
export async function getItemsBySubcategory(subcategoryId: string): Promise<any[]> {
  try {
    const response = await fetch(
      `${API_URL}/items?subcategory=${subcategoryId}`,
      { cache: 'no-store' }
    );

    if (!response.ok) return [];

    const data = await response.json();
    return data.data?.items || [];
  } catch (error) {
    console.error('Failed to fetch subcategory items:', error);
    return [];
  }
}

/**
 * Server-side fetch for virtual category metadata (no nested categories/items).
 * Keeps the SSR payload small and avoids HTTP 413 on AWS Amplify / API Gateway.
 */
export async function getVirtualCategoryById(virtualCategoryId: string): Promise<any | null> {
  try {
    const qs = new URLSearchParams({
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
    }).toString();
    const response = await fetch(`${API_URL}/virtual-categories/${virtualCategoryId}?${qs}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.success || !data.data) return null;

    return data.data;
  } catch (error) {
    console.error('Failed to fetch virtual category:', error);
    return null;
  }
}

/**
 * Server-side fetch for items by virtual category. Used for SSR.
 */
export async function getItemsByVirtualCategory(
  virtualCategoryId: string,
  params: Record<string, string> = {}
): Promise<{ items: any[]; pagination: any } | null> {
  try {
    const allParams = { virtual_category_id: virtualCategoryId, limit: '12', ...params };
    const query = new URLSearchParams(allParams).toString();
    const response = await fetch(`${API_URL}/items?${query}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.success || !data.data) return null;

    return {
      items: data.data.items || [],
      pagination: data.data.pagination || { page: 1, limit: 12, total: 0, pages: 1 },
    };
  } catch (error) {
    console.error('Failed to fetch virtual category items:', error);
    return null;
  }
}

/**
 * Server-side fetch for all categories (for filters and generateStaticParams). Used for SSR/SSG.
 */
export async function getAllCategories(params: Record<string, string> = {}): Promise<any[]> {
  try {
    const query = new URLSearchParams({
      [WEBSITE_ONLY_QUERY_PARAM]: 'true',
      ...params,
    }).toString();
    const response = await fetch(`${API_URL}/categories${query ? `?${query}` : ''}`, {
      next: { revalidate: 3600 },
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (!data.success || !data.data) return [];

    return data.data || [];
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }
}
